import React, { useState } from 'react'
import { useIsFetching, useQueryClient } from '@tanstack/react-query'
import { Building2, Home, List, Truck, User } from 'lucide-react'
import { Content } from './style'
import TableCompanies from './CompaniesTable'
import { Outlet, useNavigate, useOutletContext } from 'react-router-dom'
import { Header, Filter } from '../../../components/SubHeaderFilter/types'
import { usePersistedFilters } from '../../../hooks/usePersistedFilters'
import { TypeMainOutlet } from '..'
import { PageHeader } from '../../../components/StandardPage'
import { ViewModeSelector } from '../Pipelines/PipelineControls'
import { CompanyPersonType } from './CompaniesTable/companiesColumns'

const dynamicFilterHeaders: Header[] = [
  { key: 'phone', label: 'Telefone', type: 'string' },
  {
    key: 'email',
    label: 'E-mail',
    type: 'string',
  },
  {
    key: 'companyTypes',
    label: 'Tipo',
    type: 'string',
    selectOptions: [
      { value: 'Cliente', label: 'Cliente' },
      { value: 'Fornecedor', label: 'Fornecedor' },
      { value: 'Transportadora', label: 'Transportadora' },
      { value: 'Própria', label: 'Própria' },
    ],
  },
  { key: 'address', label: 'Endereço de faturamento', type: 'string' },
  { key: 'deliveryAddresses', label: 'Endereços de entrega', type: 'string' },
  { key: 'contribuinteIcms', label: 'Contribuinte ICMS', type: 'string' },
  { key: 'contacts', label: 'Contatos', type: 'string' },
  { key: 'linkedUsers', label: 'Usuários Vinculados', type: 'string' },
]

const buildFilterHeaders = (personType: CompanyPersonType): Header[] => {
  if (personType === 'Jurídica') {
    return [
      { key: 'cnpj', label: 'CNPJ', type: 'string' },
      { key: 'companyName', label: 'Razão Social', type: 'string' },
      ...dynamicFilterHeaders,
    ]
  }
  if (personType === 'Física') {
    return [
      { key: 'cpf', label: 'CPF', type: 'string' },
      { key: 'contactName', label: 'Nome', type: 'string' },
      ...dynamicFilterHeaders,
    ]
  }
  return [
    { key: 'cnpj', label: 'CNPJ', type: 'string' },
    { key: 'companyName', label: 'Razão Social', type: 'string' },
    { key: 'cpf', label: 'CPF', type: 'string' },
    { key: 'contactName', label: 'Nome', type: 'string' },
    ...dynamicFilterHeaders,
  ]
}

const CompaniesContent = () => {
  const { search } = useOutletContext<TypeMainOutlet>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isFetchingCompanies = useIsFetching({ queryKey: ['companies'] }) > 0
  const {
    appliedFilters,
    handleApplyFilter,
    handleRemoveFilter,
    handleClearAll
  } = usePersistedFilters('companies')
  const [personType, setPersonType] = useState<CompanyPersonType>(null)

  const handleAddFilter = (f: Filter) => {
    let cleanedValue = typeof f.value === 'string' ? f.value.replace(/\s+/g, ' ').trim() : f.value;
    let cleanedDisplay = typeof f.displayValue === 'string' ? String(f.displayValue).replace(/\s+/g, ' ').trim() : f.displayValue;

    if ((f.key === 'address' || f.key === 'deliveryAddresses') && typeof cleanedValue === 'string') {
      const digits = cleanedValue.replace(/\D/g, '');
      if (digits.length > 0) {
        const padded = digits.padStart(8, '0');
        cleanedValue = digits;
        cleanedDisplay = `${padded.slice(0, 5)}-${padded.slice(5)}`;
      }
    }

    handleApplyFilter({ ...f, value: cleanedValue, displayValue: cleanedDisplay });
  }

  const personTypeSelector = (
    <ViewModeSelector
      value={personType ?? 'Todos'}
      options={[
        { value: 'Todos', label: 'Todos', icon: <List size={16} /> },
        { value: 'Jurídica', label: 'Jurídica', icon: <Building2 size={16} /> },
        { value: 'Física', label: 'Física', icon: <User size={16} /> },
        { value: 'Própria', label: 'Própria', icon: <Home size={16} /> },
        { value: 'Transportadora', label: 'Transportadora', icon: <Truck size={16} /> },
      ]}
      onChange={(value: string) => {
        setPersonType(value === 'Todos' ? null : (value as CompanyPersonType))
      }}
    />
  )

  return (
    <>
      <PageHeader
        title='Empresas'
        tableName='Company'
        moduleName='Empresas'
        onReload={() => queryClient.invalidateQueries({ queryKey: ['companies'] })}
        isLoading={isFetchingCompanies}
        filterHeaders={buildFilterHeaders(personType)}
        onApplyFilter={handleAddFilter}
        appliedFilters={appliedFilters}
        onRemoveFilter={handleRemoveFilter}
        onClearAllFilters={() => {
          handleClearAll()
          setPersonType(null)
        }}
        createButton={{ label: 'Cadastrar empresa', onClick: () => navigate('create') }}
        rightExtra={personTypeSelector}
      />
      <Content>
        <TableCompanies
          appliedFilters={appliedFilters}
          personType={personType}
          search={search}
        />
        <Outlet
          context={search}
        />
      </Content>
    </>
  )
}
export default CompaniesContent
